import React, { useMemo, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import Modal from '../../../components/Modal';
import Button from '../../../components/Button';
import Header from './components/Header';
import Filters from './components/Filters';
import Stats from './components/Stats';
import Row from './components/Row';
import TableHeaderSection from './components/TableHeaderSection';
import TableBox from '../../../components/TableBox';
import { HeadCellType, TableContextProvider } from '../../../interfaces/common';
import { ListFilters, PropertyKPI } from '../../../interfaces/property.interface';
import { buildHeadCells } from '../../../utils/methods';
import {
  GENERIC_LOOKUP_KEYS,
  VALIDATION_FIELD_MAPPING,
  HEAD_CELL_SORT_MAPPING,
} from '../../../utils/appConstant';
import useSorting from '../../../hooks/useSorting';
import usePagination from '../../../hooks/usePagination';
import useUrlParams from '../../../hooks/useUrlParams';
import useFetch from '../../../hooks/useFetch';
import PropertyService from '../../../services/property.service';
import CommonService from '../../../services/common.service';
import DeveloperService from '../../../services/developer.service';
import UserService from '../../../services/user.service';
import { SortOrder, Modules, AuthRoutes } from '../../../enums';
import { usePersistentFilters } from '../../../hooks/usePersistentFilters';

const List: React.FC = () => {
  const { t } = useTranslation();

  const defaultFilters: ListFilters = {
    searchText: '',
    constructionStatus: '',
    approvalStatus: '',
    categoryId: '',
    stateId: '',
    cityId: [],
    developerId: [],
    amenityIds: [],
    propertyTypeId: '',
    bhkTypeIds: [],
    areaIds: [],
    status: '1',
    isFeatured: '',
    possessionDateFrom: null,
    possessionDateTo: null,
    priceMin: '',
    priceMax: '',
    reraAreaMin: '',
    reraAreaMax: '',
    measurement: 'sq_ft',
    createdBy: '',
  };

  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // Capture URL status once at mount before useUrlParams can strip it from the URL
  const mountUrlStatus = useRef(new URLSearchParams(window.location.search).get('status'));

  const { initialFilters, saveFilters, resetFilters } = usePersistentFilters<ListFilters>('filters_properties', defaultFilters);

  // Stable initial filters: URL status takes priority over localStorage on first load.
  // Computed once via lazy useState so it doesn't change when useUrlParams rewrites the URL.
  const [effectiveInitialFilters] = useState<ListFilters>(() =>
    mountUrlStatus.current !== null
      ? { ...initialFilters, status: mountUrlStatus.current }
      : initialFilters
  );

  const [filters, setFilters] = useState<ListFilters>(effectiveInitialFilters);

  const [dropdownSearchData, setDropdownSearchData] = useState<any>({
    constructionStatus: '',
    approvalStatus: '',
    category: '',
    state: '',
    city: '',
    developer: '',
    amenities: '',
    area: '',
    propertyType: '',
    bhkType: '',
    measurement: '',
    status: '',
    user: '',
  });

  const headCells: HeadCellType[] = buildHeadCells(
    [
      VALIDATION_FIELD_MAPPING.name,
      VALIDATION_FIELD_MAPPING.rera_number, // New separate column
      VALIDATION_FIELD_MAPPING.addressCity, // Location
      VALIDATION_FIELD_MAPPING.property_developer_name, // New Developer Column
      VALIDATION_FIELD_MAPPING.category,
      VALIDATION_FIELD_MAPPING.construction_status,
      VALIDATION_FIELD_MAPPING.approval_status,
      VALIDATION_FIELD_MAPPING.contact_person,
      VALIDATION_FIELD_MAPPING.status, // Active/Inactive
      VALIDATION_FIELD_MAPPING.actions,
    ],
    'propertyList'
  );

  const { order, orderBy, setOrder, setOrderBy } = useSorting({
    defaultOrder: (searchParams.get('order') as SortOrder) || SortOrder.asc,
    defaultOrderBy: searchParams.get('orderBy') || VALIDATION_FIELD_MAPPING.name,
  });
  const { page, perPage, setPage, setPerPage } = usePagination({
    defaultPage: Number(searchParams.get('page')) || 1,
  });

  useUrlParams({
    type: AuthRoutes.properties,
    uri: Modules.properties + AuthRoutes.properties,
    perPage,
    page,
    order,
    orderBy,
  });

  const {
    total,
    data: properties,
    loading,
  } = useFetch({
    service: PropertyService,
    func: 'list',
    payload: {
      page,
      perPage,
      orderBy: HEAD_CELL_SORT_MAPPING.propertyList?.[orderBy] || orderBy,
      order,
      search: filters.searchText.trim(),
      is_active: filters.status ? Number(filters.status) : '',
      is_featured: filters.isFeatured ? Number(filters.isFeatured) : '',
      category_id: filters.categoryId ? Number(filters.categoryId) : '',
      city_ids: filters.cityId?.length ? filters.cityId.map(id => Number(id)) : [],
      state_id: filters.stateId ? Number(filters.stateId) : '',
      country_id: '',
      construction_status: filters.constructionStatus || '',
      approval_status: filters.approvalStatus || '',
      developer_ids: filters.developerId?.length ? filters.developerId.map(id => Number(id)) : [],
      amenity_ids: filters.amenityIds?.length ? filters.amenityIds.map(id => Number(id)) : [],
      possession_start_date: filters.possessionDateFrom,
      possession_end_date: filters.possessionDateTo,
      property_type_ids: filters.propertyTypeId ? [Number(filters.propertyTypeId)] : [],
      bhk_type: filters.bhkTypeIds?.length ? filters.bhkTypeIds : [],
      min_price: filters.priceMin ? Number(filters.priceMin) : '',
      max_price: filters.priceMax ? Number(filters.priceMax) : '',
      min_rera_area: filters.reraAreaMin ? Number(filters.reraAreaMin) : '',
      max_rera_area: filters.reraAreaMax ? Number(filters.reraAreaMax) : '',
      measurement: filters.measurement || '',
      area_ids: filters.areaIds?.length ? filters.areaIds.map((id: any) => Number(id)) : [],
      favourite_property_ids: '',
      property_developer_name: '',
      rera_number: '',
      pincode: '',
      launch_date: '',
      possession_date: '',
      created_by: filters.createdBy || '',
      updated_by: ''
    },
    dependency: [
      order,
      orderBy,
      page,
      perPage,
      perPage,
      filters.searchText,
      filters.constructionStatus,
      filters.approvalStatus,
      filters.categoryId,
      filters.stateId,
      filters.cityId,
      filters.developerId,
      filters.amenityIds,
      filters.propertyTypeId,
      filters.bhkTypeIds,
      filters.areaIds,
      filters.status,
      filters.isFeatured,
      filters.possessionDateFrom,
      filters.possessionDateTo,
      filters.priceMin,
      filters.priceMax,
      filters.reraAreaMin,
      filters.reraAreaMax,
      filters.measurement,
      filters.createdBy,
    ],
  });

  /* KPI Data */
  const [kpiData, setKpiData] = useState<PropertyKPI | null>(null);

  useFetch({
    service: PropertyService,
    func: 'kpis',
    payload: {},
    dependency: [],
    onSuccess: (data) => {
      setKpiData(data);
    },
  });

  // Lookups
  const { data: allStates } = useFetch({
    service: CommonService,
    func: 'statesLookup',
    payload: { searchText: '', countryId: '1' },
    dependency: [],
  });

  const { data: states } = useFetch({
    service: CommonService,
    func: 'statesLookup',
    payload: { searchText: dropdownSearchData.state || '', countryId: '1' },
    dependency: [dropdownSearchData.state],
  });

  const { data: allCities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: { searchText: '', stateId: filters.stateId },
    dependency: [filters.stateId],
  });

  const { data: cities } = useFetch({
    service: CommonService,
    func: 'citiesLookup',
    payload: { searchText: dropdownSearchData.city || '', stateId: filters.stateId },
    dependency: [dropdownSearchData.city, filters.stateId],
  });

  const { data: allAreas } = useFetch({
      service: CommonService,
      func: 'areaLookup',
      payload: { searchText: '', cityId: filters.cityId },
      dependency: [filters.cityId],
  });

  const { data: areas } = useFetch({
      service: CommonService,
      func: 'areaLookup',
      payload: { searchText: dropdownSearchData.area || '', cityId: filters.cityId },
      dependency: [dropdownSearchData.area, filters.cityId],
  });

  const { data: allConstructionStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.constructionStatus, searchText: '' }, // Need to add key
    dependency: [],
  });
  
  const { data: constructionStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.constructionStatus, searchText: dropdownSearchData.constructionStatus },
    dependency: [dropdownSearchData.constructionStatus],
  });

  const { data: allApprovalStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.approvalStatus, searchText: '' },
    dependency: [],
  });
  
  const { data: approvalStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.approvalStatus, searchText: dropdownSearchData.approvalStatus },
    dependency: [dropdownSearchData.approvalStatus],
  });

   const { data: allCategories } = useFetch({
    service: CommonService,
    func: 'categoryLookup',
    payload: { searchText: '' }, 
    dependency: [],
  });

   const { data: categories } = useFetch({
    service: CommonService,
    func: 'categoryLookup',
    payload: { searchText: dropdownSearchData.category },
    dependency: [dropdownSearchData.category],
  });

  const { data: allDevelopers } = useFetch({
      service: DeveloperService,
      func: 'lookup',
      payload: { searchText: '' },
      dependency: [],
  });

  const { data: developers } = useFetch({
      service: DeveloperService,
      func: 'lookup',
      payload: { searchText: dropdownSearchData.developer },
      dependency: [dropdownSearchData.developer],
  });

  const { data: allAmenities } = useFetch({
      service: CommonService,
      func: 'amenityLookup',
      payload: { searchText: '' },
      dependency: [],
  });

  const { data: amenities } = useFetch({
      service: CommonService,
      func: 'amenityLookup',
      payload: { searchText: dropdownSearchData.amenities || '' },
      dependency: [dropdownSearchData.amenities],
  });

  const { data: allPropertyTypes } = useFetch({
      service: CommonService,
      func: 'propertyTypeLookup',
      payload: { searchText: '' },
      dependency: [],
  });

  const { data: propertyTypes } = useFetch({
      service: CommonService,
      func: 'propertyTypeLookup',
      payload: { searchText: dropdownSearchData.propertyType || '' },
      dependency: [dropdownSearchData.propertyType],
  });

  const { data: allBhkTypes } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.bhkType, searchText: '' },
    dependency: [],
  });

  const { data: bhkTypes } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.bhkType, searchText: dropdownSearchData.bhkType },
    dependency: [dropdownSearchData.bhkType],
  });

  const { data: allMeasurements } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: 'measurement', searchText: '' },
    dependency: [],
  });

  const { data: measurements } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: 'measurement', searchText: dropdownSearchData.measurement },
    dependency: [dropdownSearchData.measurement],
  });

  const { data: allStatus } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: '' },
    dependency: [],
  });

  const { data: status } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.status, searchText: dropdownSearchData.status },
    dependency: [dropdownSearchData.status],
  });

  const { data: allUsers } = useFetch({
    service: UserService,
    func: 'lookup',
    payload: { searchText: '', is_active: '1' },
    dependency: [],
  });

  const { data: users } = useFetch({
    service: UserService,
    func: 'lookup',
    payload: { searchText: dropdownSearchData.user || '', is_active: '1' },
    dependency: [dropdownSearchData.user],
  });


  const handleApplyFilters = useCallback((newFilters: ListFilters) => {
    setPage(1);
    setOrder(SortOrder.asc);
    setOrderBy(VALIDATION_FIELD_MAPPING.name);
    setFilters(newFilters);
    saveFilters(newFilters);
  }, [saveFilters, setPage, setOrder, setOrderBy]);

  const handleResetFilters = useCallback(() => {
    setPage(1);
    setOrder(SortOrder.asc);
    setOrderBy(VALIDATION_FIELD_MAPPING.name);
    setFilters(defaultFilters);
    resetFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetFilters, setPage, setOrder, setOrderBy]);

  const [deleteTarget, setDeleteTarget] = useState<{ open: boolean; item: any | null }>({
    open: false,
    item: null,
  });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handleDeleteClick = useCallback((item: any) => {
    setDeleteTarget({ open: true, item });
  }, []);

  const handleDeleteClose = useCallback(() => {
    setDeleteTarget({ open: false, item: null });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget.item) return;
    setDeleteLoading(true);
    try {
      await PropertyService.deleteProperty(deleteTarget.item.id);
      handleDeleteClose();
      setPage(1);
    } catch {
      // error handled by global axios interceptor
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteTarget.item, handleDeleteClose, setPage]);

  const RowWithDelete = useCallback(
    (props: any) => <Row {...props} onDeleteClick={handleDeleteClick} />,
    [handleDeleteClick]
  );

  const handleDropdownSearchInputChange = useCallback(
    (event: any, name: string) => {
      setDropdownSearchData((prev: any) => {
        return {
          ...prev,
          [name]: event.target.value,
        };
      });
    },
    [setDropdownSearchData]
  );

  const values: TableContextProvider = useMemo(() => {
    const headerSection = <TableHeaderSection total={total} kpis={kpiData} />;
    return {
      headerSection,
      row: RowWithDelete,
      data: properties || [],
      pagination: true,
      headCells,
      order,
      orderBy,
      page,
      perPage,
      total,
      loading,
      noDataFoundMessage: t('properties.list.pageTexts.table.noDataFoundMessage'),
      setPage,
      setPerPage,
      setOrder,
      setOrderBy,
    };
  }, [
    t,
    headCells,
    kpiData,
    order,
    orderBy,
    page,
    perPage,
    total,
    loading,
    properties,
    setPage,
    setPerPage,
    setOrder,
    setOrderBy,
    RowWithDelete,
  ]);

  return (
    <>
      <Header />

      <Stats data={kpiData} />

      <Filters
         constructionStatus={constructionStatus}
         allConstructionStatus={allConstructionStatus}
         approvalStatus={approvalStatus}
         allApprovalStatus={allApprovalStatus}
         categories={categories}
         allCategories={allCategories}
         states={states}
         allStates={allStates}
         cities={cities}
         allCities={allCities}
         developers={developers}
         allDevelopers={allDevelopers}
         amenities={amenities}
         allAmenities={allAmenities}
         propertyTypes={propertyTypes}
         allPropertyTypes={allPropertyTypes}
         bhkTypes={bhkTypes}
         allBhkTypes={allBhkTypes}
         measurements={measurements}
         allMeasurements={allMeasurements}
         areas={areas}
         allAreas={allAreas}
         status={status}
         allStatus={allStatus}
         users={users}
         allUsers={allUsers}
         dropdownSearchData={dropdownSearchData}
         initialFilters={effectiveInitialFilters}
         onDropdownSearchChange={handleDropdownSearchInputChange}
         onApply={handleApplyFilters}
         onReset={handleResetFilters}
      />
      <TableBox tableValues={values} />

      <Modal
        open={deleteTarget.open}
        onClose={handleDeleteClose}
        title={t('common.modal.deleteConfirmationTitle') || 'Delete Confirmation'}
        maxWidth="sm"
        content={
          <div className="text-center">
            <i className="ri-error-warning-line text-warning fs-1 mb-3"></i>
            <p className="mb-1 fw-semibold">{deleteTarget.item?.name}</p>
            <p className="mb-0">
              {t('common.modal.propertyDeleteConfirmationMessage') ||
                'Are you sure you want to delete this property? This action cannot be undone.'}
            </p>
          </div>
        }
        action={
          <>
            <Button
              className="btn btn-label-secondary waves-effect"
              onClick={handleDeleteClose}
              disabled={deleteLoading}
              text={t('common.buttons.cancel') || 'Cancel'}
            />
            <Button
              className="btn btn-danger waves-effect"
              onClick={handleDeleteConfirm}
              disabled={deleteLoading}
              text={deleteLoading ? (t('common.buttons.deleting') || 'Deleting…') : (t('common.buttons.delete') || 'Delete')}
            />
          </>
        }
      />
    </>
  );
};

export default List;
